import React, { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { Navigate, useParams } from 'react-router-dom';

import NotFoundPage from '~/blocks/NotFoundPage/NotFoundPage';
import Loader from '~/components/Loader/Loader';
import RouteWithCode from '~/components/RouteWithCode/RouteWithCode';
import { LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME } from '~/const';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import {
  GET_CASE_STUDIES_ATTEMPTS,
  GET_PATHWAY_PAGE_INFO,
  GET_PATHWAY_PAGE_PROGRESS,
} from '~/graphql/pathways';
import useIsSSR from '~/hooks/useIsSSR';
import { buildCaseStudyRoute, buildPathwayTestRoute } from '~/routes';
import {
  CaseStudyPassedResult,
  Query,
  QueryGetCaseStudiesAttemptsArgs,
  QueryGetPathwayLearnerPageInfoArgs,
  QueryGetPathwayPageInfoArgs,
} from '~/types';
import Cookie from '~/utils/cookies';
import bindProgressToLearningModule, {
  LearningModuleWithProgress,
} from '~/utils/learningModules/bindProgressToLearningModule';
import isLearningModuleCompleted from '~/utils/learningModules/isLearningModuleCompleted';
import { toBoolean } from '~/utils/toBoolean';
import { toProgressByVideoId } from '~/utils/videoProgressUtils';

const ContinueLearning = () => {
  const params = useParams();
  const slug = params?.slug || '';
  const { userInfo } = useUserInfoContext();
  const isSSR = useIsSSR();

  const { data: pathwayProgressData, loading: loadingProgressData } = useQuery<
    Pick<Query, 'getPathwayLearnerPageInfo'>,
    QueryGetPathwayLearnerPageInfoArgs
  >(GET_PATHWAY_PAGE_PROGRESS, {
    variables: {
      filter: {
        slug: [slug],
      },
    },
    skip: !userInfo || isSSR || !slug,
    fetchPolicy: 'network-only',
  });

  const {
    data: pathwayData,
    loading: loadingPathwayData,
    called,
  } = useQuery<Pick<Query, 'getPathwayPageInfo'>, QueryGetPathwayPageInfoArgs>(
    GET_PATHWAY_PAGE_INFO,
    {
      variables: {
        filter: {
          slug: [slug],
        },
      },
      fetchPolicy: 'network-only',
      skip: !slug,
    },
  );

  const pathwayId = pathwayData?.getPathwayPageInfo?.pathway?.id;

  const { data: caseStudiesProgressData, loading: loadingCaseStudiesProgressData } = useQuery<
    Pick<Query, 'getCaseStudiesAttempts'>,
    QueryGetCaseStudiesAttemptsArgs
  >(GET_CASE_STUDIES_ATTEMPTS, {
    variables: {
      pathway_ids: [Number(pathwayId)],
    },
    skip: !pathwayId || isSSR,
    fetchPolicy: 'network-only',
  });

  const progressData = toProgressByVideoId(
    pathwayProgressData?.getPathwayLearnerPageInfo?.progress,
  );
  const pathway = pathwayData?.getPathwayPageInfo?.pathway;
  const test = pathwayProgressData?.getPathwayLearnerPageInfo?.pathway?.available_quizes?.[0];
  const caseStudyAttempts =
    caseStudiesProgressData?.getCaseStudiesAttempts?.filter(toBoolean<CaseStudyPassedResult>) || [];
  const learningModulesWithProgress =
    pathway?.learning_modules
      ?.map(
        bindProgressToLearningModule({
          caseStudyAttempts,
          progressDataByVideoId: progressData,
        }),
      )
      .filter(toBoolean<LearningModuleWithProgress>) || [];
  const nextUncompletedLearningModule = learningModulesWithProgress.find(
    (item) => !isLearningModuleCompleted(item),
  );
  useEffect(() => {
    if (!slug) {
      return;
    }

    Cookie.set(LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME, slug);
  }, [slug]);

  const link = nextUncompletedLearningModule
    ? nextUncompletedLearningModule.__typename === 'Video'
      ? nextUncompletedLearningModule.link
      : buildCaseStudyRoute(pathway?.slug, nextUncompletedLearningModule?.id)
    : test && buildPathwayTestRoute(slug, test.id);

  if (called && !pathwayData?.getPathwayPageInfo && !loadingPathwayData) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  if (loadingProgressData || loadingPathwayData || !userInfo || loadingCaseStudiesProgressData) {
    return <Loader />;
  }

  if (link) {
    return <Navigate to={link} replace />;
  }

  return <Loader />;
};

export default ContinueLearning;
